/**
 * Relationship Exercises - Exercises for different relationship types
 * Integrated with progress tracking, achievements, and partner linking
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Heart, Users, Briefcase, Home, MessageCircle, 
  Shield, Zap, Target, HandHeart,
  ChevronRight, ChevronDown, Clock, Sparkles, CheckCircle2, Trophy
} from 'lucide-react';
import { RelationshipExerciseModal } from './RelationshipExerciseModal';
import { PartnerLinkCard } from './PartnerLinkCard';
import { RelationshipAssessmentCard } from './RelationshipAssessmentCard';
import { useRelationalProgress } from '@/hooks/useRelationalProgress';

interface RelationshipExercisesProps {
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other';
  workingMode: 'solo' | 'together';
}

interface Exercise {
  id: string;
  title: string;
  description: string;
  duration: string;
}

interface ExerciseCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  exercises: Exercise[];
}

const RELATIONSHIP_CATEGORIES: Record<string, ExerciseCategory[]> = {
  romantic: [
    {
      id: 'communication',
      title: 'Communication Skills',
      description: 'Deepen understanding and connection through better communication',
      icon: MessageCircle,
      exercises: [
        { id: 'active-listening', title: 'Active Listening Practice', description: 'Learn to truly hear your partner', duration: '15 min' },
        { id: 'love-languages', title: 'Love Languages Discovery', description: 'Understand how you each give and receive love', duration: '20 min' },
        { id: 'weekly-check-in', title: 'Weekly Check-In Structure', description: 'A framework for regular connection conversations', duration: '30 min' },
      ],
    },
    {
      id: 'conflict',
      title: 'Conflict Resolution',
      description: 'Navigate disagreements with respect and understanding',
      icon: Shield,
      exercises: [
        { id: 'repair-attempts', title: 'Repair Attempts', description: 'De-escalate conflicts before they grow', duration: '10 min' },
        { id: 'fair-fighting', title: 'Fair Fighting Rules', description: 'Establish healthy conflict guidelines', duration: '15 min' },
        { id: 'processing-conflict', title: 'Processing Conflict Together', description: 'Work through disagreements productively', duration: '30 min' },
      ],
    },
    {
      id: 'intimacy',
      title: 'Emotional Intimacy',
      description: 'Build deeper emotional connection and vulnerability',
      icon: Heart,
      exercises: [
        { id: 'appreciation-practice', title: 'Daily Appreciation Practice', description: 'Express gratitude for your partner', duration: '5 min' },
        { id: 'vulnerability-shares', title: 'Vulnerability Sharing', description: 'Practice opening up safely', duration: '20 min' },
        { id: 'dreams-within', title: 'Dreams Within Conflict', description: 'Understand the deeper meaning behind disagreements', duration: '25 min' },
      ],
    },
  ],
  family: [
    {
      id: 'boundaries',
      title: 'Family Boundaries',
      description: 'Establish healthy boundaries with family members',
      icon: Shield,
      exercises: [
        { id: 'identifying-boundaries', title: 'Identifying Needed Boundaries', description: 'Clarify where you need protection', duration: '15 min' },
        { id: 'communicating-boundaries', title: 'Communicating Boundaries', description: 'Express limits with love', duration: '20 min' },
        { id: 'maintaining-boundaries', title: 'Maintaining Boundaries', description: 'Stay firm when tested', duration: '15 min' },
      ],
    },
    {
      id: 'patterns',
      title: 'Family Patterns',
      description: 'Understand and transform inherited patterns',
      icon: Users,
      exercises: [
        { id: 'empathy-practice', title: 'Empathy Practice', description: 'See from their perspective', duration: '15 min' },
        { id: 'response-not-react', title: 'Respond, Do Not React', description: 'Choose thoughtful responses', duration: '15 min' },
        { id: 'repair-relationships', title: 'Relationship Repair', description: 'Heal after conflict or hurt', duration: '20 min' },
      ],
    },
  ],
  work: [
    {
      id: 'assertiveness',
      title: 'Workplace Assertiveness',
      description: 'Stand up for yourself professionally',
      icon: Zap,
      exercises: [
        { id: 'broken-record', title: 'Broken Record Technique', description: 'Persist calmly with your message', duration: '10 min' },
        { id: 'fogging', title: 'Fogging Technique', description: 'Deflect criticism without escalation', duration: '10 min' },
        { id: 'assertive-requests', title: 'Making Assertive Requests', description: 'Ask for what you need clearly', duration: '15 min' },
      ],
    },
    {
      id: 'difficult-people',
      title: 'Difficult Colleagues',
      description: 'Navigate challenging workplace relationships',
      icon: Target,
      exercises: [
        { id: 'grey-rock', title: 'Grey Rock Method', description: 'Become uninteresting to difficult people', duration: '10 min' },
        { id: 'documentation', title: 'Documentation Practice', description: 'Protect yourself professionally', duration: '15 min' },
        { id: 'limiting-exposure', title: 'Strategic Distance', description: 'Minimise harmful interactions', duration: '10 min' },
      ],
    },
    {
      id: 'self-advocacy',
      title: 'Self-Advocacy',
      description: 'Advance your interests at work',
      icon: HandHeart,
      exercises: [
        { id: 'response-not-react', title: 'Respond, Do Not React', description: 'Choose thoughtful responses', duration: '15 min' },
        { id: 'empathy-practice', title: 'Empathy Practice', description: 'Understand others perspectives', duration: '15 min' },
        { id: 'repair-relationships', title: 'Professional Repair', description: 'Heal work relationships after conflict', duration: '20 min' },
      ],
    },
  ],
  friendship: [
    {
      id: 'deepening',
      title: 'Deepening Friendships',
      description: 'Build more meaningful connections',
      icon: Heart,
      exercises: [
        { id: 'vulnerability-friends', title: 'Vulnerability with Friends', description: 'Share more of yourself', duration: '15 min' },
        { id: 'quality-time', title: 'Intentional Quality Time', description: 'Be fully present together', duration: '20 min' },
        { id: 'reciprocity-check', title: 'Reciprocity Check', description: 'Assess the balance of give and take', duration: '15 min' },
      ],
    },
    {
      id: 'boundaries-friends',
      title: 'Friendship Boundaries',
      description: 'Navigate complex friend dynamics',
      icon: Shield,
      exercises: [
        { id: 'identifying-boundaries', title: 'Recognising Needed Boundaries', description: 'Identify where you need protection', duration: '15 min' },
        { id: 'communicating-boundaries', title: 'Expressing Boundaries', description: 'Share limits with care', duration: '20 min' },
        { id: 'response-not-react', title: 'Thoughtful Responses', description: 'Respond rather than react', duration: '15 min' },
      ],
    },
  ],
  other: [
    {
      id: 'general',
      title: 'Universal Relationship Skills',
      description: 'Skills that apply to any relationship',
      icon: Users,
      exercises: [
        { id: 'empathy-practice', title: 'Empathy Practice', description: 'See from their perspective', duration: '15 min' },
        { id: 'response-not-react', title: 'Respond, Do Not React', description: 'Choose thoughtful responses', duration: '15 min' },
        { id: 'repair-relationships', title: 'Relationship Repair', description: 'Heal after conflict or hurt', duration: '20 min' },
      ],
    },
  ],
};

export function RelationshipExercises({ relationshipType, workingMode }: RelationshipExercisesProps) {
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [isExercisesOpen, setIsExercisesOpen] = useState(false);
  const { 
    completeExercise, 
    getCompletedExerciseIds, 
    getStats 
  } = useRelationalProgress();
  
  const completedIds = getCompletedExerciseIds();
  const stats = getStats();
  const categories = RELATIONSHIP_CATEGORIES[relationshipType] || RELATIONSHIP_CATEGORIES.other;

  const totalExercises = categories.reduce((sum, cat) => sum + cat.exercises.length, 0);
  const completedCount = categories.reduce((sum, cat) => 
    sum + cat.exercises.filter(e => completedIds.has(e.id)).length, 0
  );
  const completionRate = totalExercises > 0 ? (completedCount / totalExercises) * 100 : 0;

  const handleExerciseComplete = async () => {
    if (!selectedExercise) return;
    
    await completeExercise(
      selectedExercise.id,
      'relationship',
      {},
      relationshipType
    );
    setSelectedExercise(null);
  };

  const getRelationshipLabel = () => {
    switch (relationshipType) {
      case 'romantic': return 'Romantic Relationship';
      case 'family': return 'Family Dynamics';
      case 'work': return 'Workplace Relationships';
      case 'friendship': return 'Friendships';
      default: return 'Relationships';
    }
  };

  return (
    <div className="space-y-6">
      {/* Expandable Exercise Card */}
      <Collapsible open={isExercisesOpen} onOpenChange={setIsExercisesOpen}>
        <Card className="bg-gradient-to-br from-secondary/10 to-accent/5 border-secondary/20">
          <CollapsibleTrigger asChild>
            <CardContent className="pt-6 cursor-pointer hover:bg-secondary/5 transition-colors">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-secondary/20">
                  <Heart className="w-6 h-6 text-secondary" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground">{getRelationshipLabel()}</h3>
                      <Badge variant="secondary" className="text-xs bg-secondary/20 text-foreground">
                        {workingMode === 'solo' ? 'Working Solo' : 'Working Together'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-primary">
                        <Trophy className="w-4 h-4" />
                        <span className="font-semibold">{stats.totalPoints}</span>
                      </div>
                      {isExercisesOpen ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {workingMode === 'solo' 
                      ? 'These exercises are designed to do together with your partner.'
                      : 'These exercises are designed to do together with your partner.'}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{completedCount} of {totalExercises} completed</span>
                      <span className="font-medium text-secondary">{Math.round(completionRate)}%</span>
                    </div>
                    <Progress value={completionRate} className="h-2" />
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="px-6 pb-6 space-y-4">
              {/* Exercise Categories */}
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <Card key={category.id} className="bg-card/50">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{category.title}</CardTitle>
                          <CardDescription className="text-xs">{category.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {category.exercises.map((exercise) => {
                          const isCompleted = completedIds.has(exercise.id);
                          return (
                            <div
                              key={exercise.id}
                              className={`flex items-center justify-between p-3 rounded-lg border hover:bg-muted/20 transition-colors cursor-pointer ${
                                isCompleted ? 'bg-muted/10 border-accent/30' : ''
                              }`}
                              onClick={() => setSelectedExercise(exercise)}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-sm">{exercise.title}</h4>
                                  {isCompleted && (
                                    <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">{exercise.description}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="text-xs gap-1">
                                  <Clock className="w-3 h-3" />
                                  {exercise.duration}
                                </Badge>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Partner Linking - for romantic relationships */}
      {relationshipType === 'romantic' && (
        <PartnerLinkCard relationshipType={relationshipType} />
      )}

      {/* Relationship Assessment */}
      <RelationshipAssessmentCard relationshipType={relationshipType} />

      {/* Gamification Teaser */}
      <Card className="bg-gradient-to-br from-primary/10 to-secondary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/20">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">Track Your Progress</h4>
              <p className="text-sm text-muted-foreground">
                Complete exercises to earn points, unlock achievements, and see your relationship 
                skills grow over time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exercise Modal */}
      {selectedExercise && (
        <RelationshipExerciseModal
          exercise={selectedExercise}
          open={!!selectedExercise}
          onOpenChange={(open) => !open && setSelectedExercise(null)}
          onComplete={handleExerciseComplete}
          isCompleted={completedIds.has(selectedExercise.id)}
          relationshipType={relationshipType}
        />
      )}
    </div>
  );
}
